/**
 * Module: `queries/conversation.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import { queryOptions } from "@tanstack/react-query";

import { PENDING_NEW_CONVERSATION_SENTINEL } from "@/constants";
import { apiClient } from "@/lib/api";
import { minutesTOms } from "@/lib/utils";
import type { DustConversation } from "@/types";

/** IDs produced by dust-api `mock-chat` — no Dust record; never call getConversation. */
export function isMockDustConversationId(id: string): boolean {
  return id.startsWith("mock-conv-");
}

/** Client-only IDs that must never be hydrated or persisted as Dust conversations. */
export function isEphemeralChatConversationId(id: string): boolean {
  return (
    id === PENDING_NEW_CONVERSATION_SENTINEL || isMockDustConversationId(id)
  );
}

export function resolvePersistableConversationId(
  selectedConversationId: string | undefined,
  streamingConversationId: string | null
): string | undefined {
  const streaming =
    streamingConversationId &&
    !isEphemeralChatConversationId(streamingConversationId)
      ? streamingConversationId
      : undefined;
  const selected =
    selectedConversationId &&
    !isEphemeralChatConversationId(selectedConversationId)
      ? selectedConversationId
      : undefined;

  if (streaming && selected && streaming !== selected) {
    return streaming;
  }
  return selected ?? streaming;
}

/**
 * Shared query options for a single Dust conversation (messages + CoT).
 * Used by the conversation route loader (prefetch) and useChat (useQuery).
 */
export function conversationQueryOptions(cId: string) {
  return queryOptions({
    queryKey: ["conversation", cId],
    queryFn: (): Promise<DustConversation> =>
      isEphemeralChatConversationId(cId)
        ? Promise.resolve({ sId: cId, content: [] })
        : apiClient.getDustConversation(cId),
    staleTime: minutesTOms(2),
    gcTime: minutesTOms(30),
  });
}
